import React from "react";
import { ManageJobsTable, ManageJobsTableItem } from "components/table";
import styles from "./styles.module.scss";
import { useState, useEffect, useRef } from "react";
import { EmptyTable } from "components/empty-table";
import { emptyReport2, SearchIconTwo } from "assets";
import { Button } from "components/button";
import { Input } from "components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Routes } from "router";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useManageJobs, useManageJobsCounts } from "hooks";
import { Spinner } from "components/loader";

const searchSchema = Yup.object().shape({
  search: Yup.string().trim(),
});

type FilterStatus = "all" | "active" | "paused" | "scheduled" | "closed" | "draft";

const TableSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get filter, search, and page from URL or use defaults
  const filterFromUrl = (searchParams.get("filter") as FilterStatus) || "all";
  const searchFromUrl = searchParams.get("search") || "";
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  
  const { register, formState: { errors }, watch } = useForm({
    resolver: yupResolver(searchSchema),
    mode: "onBlur",
    defaultValues: {
      search: searchFromUrl,
    },
  });
  const watchSearch = watch("search");
  
  const [currentFilter, setCurrentFilter] = useState<FilterStatus>(filterFromUrl);
  const [searchTerm, setSearchTerm] = useState<string>(searchFromUrl);
  const [currentPage, setCurrentPage] = useState<number>(pageFromUrl);
  const pageSize = 10;
  
  // Track if component has initialized to avoid overwriting URL on mount
  const hasInitialized = useRef(false);

  // Set initialization flag after mount
  useEffect(() => {
    hasInitialized.current = true;
  }, []);

  // Set default filter in URL if not present
  useEffect(() => {
    if (!searchParams.get("filter")) {
      const params = new URLSearchParams();
      params.set("filter", "all");
      params.set("page", "1");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL params with current state (skip on initial mount)
  useEffect(() => {
    if (!hasInitialized.current) return;
    
    const params = new URLSearchParams();
    params.set("filter", currentFilter);
    params.set("page", currentPage.toString());
    if (searchTerm) {
      params.set("search", searchTerm);
    }
    setSearchParams(params, { replace: true });
  }, [currentFilter, searchTerm, currentPage, setSearchParams]);

  // Fetch job counts for all statuses
  const { counts } = useManageJobsCounts();

  // Fetch jobs with current filter, search, and pagination
  const { jobs, totalCount, totalPages, isLoading } = useManageJobs({
    search: searchTerm,
    status: currentFilter,
    page: currentPage,
    pageSize,
  });

  // Transform API data to table format
  const filteredData: ManageJobsTableItem[] = jobs.map((job, index) => {
    // Format published date to MM/DD/YYYY
    const formatDate = (dateString: string | null) => {
      if (!dateString) return "-";
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    };

    return {
      id: ((currentPage - 1) * pageSize + index + 1).toString(),
      assessmentId: job.assessment_id || job.id?.toString(),
      role: job.title,
      publishedDate: formatDate(job.published_at),
      viewsVisits: job.no_of_visits,
      applications: job.no_of_applications,
      status: (job.status.charAt(0).toUpperCase() + job.status.slice(1)) as ManageJobsTableItem["status"],
      currentStage: "N/A", // API doesn't provide this yet
      startDate: formatDate(job.published_at),
      endDate: formatDate(job.deadline ?? null),
      deadline: formatDate(job.deadline ?? null),
      deadlineRaw: job.deadline ?? null,
      assessmentLink: job.assessment_link,
    };
  });

  const handleFilter = async (status: FilterStatus) => {
    setCurrentFilter(status);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Watch for search input changes and update search term with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(watchSearch?.trim() || "");
      setCurrentPage(1); // Reset to first page when search changes
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [watchSearch]);

  // Get filtered data count for each status from API
  const getStatusCount = (status: FilterStatus): number => {
    if (status === "all") {
      return counts.total;
    }
    return counts[status];
  };

  return (
    <div className=''>
      <div className="w-full overflow-x-auto">
        <div className={styles.filterSection}>
          <div className={styles.buttons}>
            <Button
              type="transparent"
              onClick={() => handleFilter("all")}
              className={`${styles.filterButton} ${currentFilter === "all" ? styles.active : ""}`}
              data-tour-id="tour-tab-all"
            >
              All <span className={styles.number}>{getStatusCount("all")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("active")}
              className={`${styles.filterButton} ${currentFilter === "active" ? styles.active : ""}`}
              data-tour-id="tour-tab-active"
            >
              Active <span className={styles.number}>{getStatusCount("active")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("paused")}
              className={`${styles.filterButton} ${currentFilter === "paused" ? styles.active : ""}`}
              data-tour-id="tour-tab-paused"
            >
              Paused <span className={styles.number}>{getStatusCount("paused")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("scheduled")}
              className={`${styles.filterButton} ${currentFilter === "scheduled" ? styles.active : ""}`}
              data-tour-id="tour-tab-scheduled"
            >
              Scheduled <span className={styles.number}>{getStatusCount("scheduled")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("closed")}
              className={`${styles.filterButton} ${currentFilter === "closed" ? styles.active : ""}`}
              data-tour-id="tour-tab-closed"
            >
              Closed <span className={styles.number}>{getStatusCount("closed")}</span>
            </Button>
            <Button
              type="transparent"
              onClick={() => handleFilter("draft")}
              className={`${styles.filterButton} ${currentFilter === "draft" ? styles.active : ""}`}
              data-tour-id="tour-tab-draft"
            >
              Drafts <span className={styles.number}>{getStatusCount("draft")}</span>
            </Button>
          </div>

          <Input
            Icon={SearchIconTwo}
            iconClassName={styles.searchIcon}
            placeholder="Search"
            type="search"
            name="search"
            register={register}
            validatorMessage={errors.search?.message || ''}
            showRequired={false}
            parentClassName={`ml-3 !rounded-[4px] min-w-[120px] ${styles.searchInputWrapper}`}
            className={`!h-[39px] ${styles.searchInput}`}
          />
        </div>
      </div>

      <div className="w-full overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-10">
            <Spinner />
          </div>
        ) : (
          <ManageJobsTable
            tableBodyItems={filteredData}
            handleView={(id: string, assessmentId?: string) => {
              // Preserve current URL params for back navigation
              const currentParams = searchParams.toString();
              const reviewTalentUrl = Routes.reviewTalents(assessmentId || id);
              navigate(`${reviewTalentUrl}${currentParams ? `?from=${encodeURIComponent(`/employer/manage-jobs?${currentParams}`)}` : ''}`);
            }}
            currentFilter={currentFilter.charAt(0).toUpperCase() + currentFilter.slice(1)}
            pagination={{
              current: currentPage,
              total: totalPages,
              handleChange: (page: number) => setCurrentPage(page),
              count: totalCount,
              length: pageSize,
            }}
            emptyState={
              filteredData.length === 0 ? (
                <EmptyTable
                  image={emptyReport2}
                  text={currentFilter === "all" ? "No jobs found" : `No ${currentFilter} jobs found`}
                />
              ) : undefined
            }
          />
        )}
      </div>

    </div>
  )
}

export { TableSection };